import React, { useState, useRef, useEffect } from "react";

const vitalOptions = {
  temperature: ["96", "98", "100", "101", "102"],
  pulse: ["40", "60", "80", "100", "120"],
  respiratoryRate: ["12", "16", "20", "24", "28"],
  bloodPressure: ["80/120", "90/130", "100/140", "110/150"],
  spo2: ["90", "92", "94", "96", "98", "100"],
  height: ["150", "160", "170", "180", "190"],
  weight: ["50", "60", "70", "80", "90"],
};

const unitOptions = {
  temperature: ["°C", "°F"],
  height: ["cm", "feet"],
  weight: ["kg", "lb"],
};

const Vitals = ({ setVitals }) => {
  const [vitalData, setVitalData] = useState([
    { label: "Temperature", value: "", unit: "°F", key: "temperature" },
    { label: "Pulse", value: "", unit: "bpm", key: "pulse" },
    {
      label: "Respiratory Rate",
      value: "",
      unit: "rpm",
      key: "respiratoryRate",
    },
    { label: "Blood Pressure", value: "", unit: "mmHg", key: "bloodPressure" },
    { label: "SPO2", value: "", unit: "%", key: "spo2" },
    {
      label: "Height",
      value: "",
      unit: "cm",
      key: "height",
      ftValue: "",
      inValue: "",
    },
    { label: "Weight", value: "", unit: "kg", key: "weight" },
  ]);

  const [focusedInput, setFocusedInput] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    setVitals(vitalData);
  }, [vitalData, setVitals]);

  const handleVitalChange = (index, value) => {
    const updatedVitalData = [...vitalData];
    updatedVitalData[index].value = value;
    setVitalData(updatedVitalData);
    setVitals(updatedVitalData);
  };

  const handleUnitChange = (index, unit) => {
    const updatedVitalData = [...vitalData];
    updatedVitalData[index].unit = unit;
    setVitalData(updatedVitalData);
    setVitals(updatedVitalData);
  };

  const handleSuggestionClick = (index, suggestion) => {
    const updatedVitalData = [...vitalData];
    updatedVitalData[index].value = suggestion;
    setVitalData(updatedVitalData);
    setVitals(updatedVitalData);
    setFocusedInput(null); // Close the dropdown after selecting a suggestion
  };

  const handleHeightChange = (ftValue, inValue) => {
    const updatedVitalData = [...vitalData];
    updatedVitalData.forEach((vital) => {
      if (vital.key === "height") {
        vital.ftValue = ftValue;
        vital.inValue = inValue;
        vital.value = `${ftValue} ft ${inValue} in`;
      }
    });
    setVitalData(updatedVitalData);
    setVitals(updatedVitalData);
  };

  const handleClickOutside = (event) => {
    if (!inputRefs.current.some((ref) => ref && ref.contains(event.target))) {
      setFocusedInput(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Vitals</h2>
        <hr className="border-gray-300" />
      </div>
      <div className="grid grid-cols-1 gap-6">
        {vitalData.map((vital, index) => (
          <div
            key={vital.key}
            className="grid grid-cols-[1fr_150px_100px] items-center gap-4"
          >
            <label className="font-medium text-gray-700">{vital.label}</label>
            {vital.unit === "feet" ? (
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="input-control w-1/2"
                  value={vital.ftValue}
                  onChange={(e) =>
                    handleHeightChange(e.target.value, vital.inValue)
                  }
                  placeholder="ft"
                />
                <input
                  type="text"
                  className="input-control w-1/2"
                  value={vital.inValue}
                  onChange={(e) =>
                    handleHeightChange(vital.ftValue, e.target.value)
                  }
                  placeholder="in"
                />
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  className="input-control w-full"
                  value={vital.value}
                  onChange={(e) => handleVitalChange(index, e.target.value)}
                  placeholder={`Enter ${vital.label}`}
                  onFocus={() => setFocusedInput(vital.key)}
                  ref={(el) => (inputRefs.current[index] = el)}
                />
                {focusedInput === vital.key && vitalOptions[vital.key] && (
                  <div
                    className="absolute left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 w-full max-w-[8ch]"
                    style={{
                      top: inputRefs.current[index].offsetHeight + "px",
                      left: "0",
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent closing dropdown when clicking on it
                  >
                    {vitalOptions[vital.key].map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className="px-3 py-1.5 cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionClick(index, option);
                        }}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {unitOptions[vital.key] ? (
              <div className="relative">
                <select
                  className="input-control w-full max-w-[8ch]"
                  value={vital.unit}
                  onChange={(e) => handleUnitChange(index, e.target.value)}
                >
                  {unitOptions[vital.key].map((unit, idx) => (
                    <option key={idx} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <span className="text-gray-600">{vital.unit}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Vitals;
